import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Grid, MenuItem, Snackbar, Stack, TextField, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import GlassCard from "../../components/dashboard/GlassCard";
import FilterBar from "../../components/enterprise/FilterBar";
import MonitoringModulePage from "./MonitoringModulePage";
import { getMonitoringCameras } from "../../services/real/monitoringCameras";
import saasApi from "../../utils/saasApi";

const apiBase = (process.env.REACT_APP_API_BASE || "").replace(/\/v1\/api\/?$/, "").replace(/\/$/, "");

const parseMetadata = (clip) => {
  if (!clip?.metadata) return {};
  if (typeof clip.metadata === "object") return clip.metadata;
  try { return JSON.parse(clip.metadata); } catch { return {}; }
};

const clipKind = (clip) => {
  const metadata = parseMetadata(clip);
  return metadata.recording_variant || metadata.variant || clip.recording_variant || clip.kind || "clip";
};

const directClipUrl = (clip) => {
  if (clip.public_url) return clip.public_url;
  if (clip.storage_key && /^https?:\/\//i.test(clip.storage_key)) return clip.storage_key;
  return "";
};

// <video> y los enlaces no mandan el token de sesion, asi que para los clips
// servidos por el backend se pide un enlace temporal y se usa ese.
const resolveClipUrl = async (clip) => {
  const direct = directClipUrl(clip);
  if (direct) return direct;
  if (!clip.id) return "";
  const link = await saasApi.createClipLink(clip.id, { minutes: 60 });
  return link?.token ? `${apiBase}/api/public/clips/${link.token}` : link?.url || "";
};

const clipCameraName = (clip) => clip.camera_name || parseMetadata(clip).camera_name || "Camara";

const sameCamera = (clip, selected) => {
  if (!selected) return true;
  return String(clip.camera_id || "") === String(selected.id || "") ||
    String(clip.camera_name || "").toLowerCase() === String(selected.name || selected.display_name || "").toLowerCase();
};

const inDateRange = (clip, from, to) => {
  const value = clip.started_at || clip.occurred_at || clip.created_at;
  if (!value) return true;
  const date = new Date(value);
  if (from && date < new Date(`${from}T00:00:00`)) return false;
  if (to && date > new Date(`${to}T23:59:59`)) return false;
  return true;
};

const RecordingsPage = () => {
  const [clips, setClips] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [variant, setVariant] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const selectedCamera = useMemo(() => cameras.find((camera) => String(camera.id) === String(selectedCameraId)), [cameras, selectedCameraId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cameraPayload, clipPayload] = await Promise.all([
        getMonitoringCameras().catch(() => ({ cameras: [] })),
        saasApi.getClips({
          status: "available",
          camera_id: selectedCameraId,
          date_from: from,
          date_to: to,
          recording_variant: variant,
          source: "manual_recording",
        }),
      ]);
      setCameras(cameraPayload.cameras || []);
      setClips(clipPayload || []);
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [from, selectedCameraId, to, variant]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openClip = async (clip, { download = false } = {}) => {
    try {
      const url = await resolveClipUrl(clip);
      if (!url) throw new Error("La grabacion no tiene archivo");
      if (download) window.open(url, "_blank", "noopener");
      else setSelected({ ...clip, playUrl: url });
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
  };

  const filtered = useMemo(() => clips.filter((clip) => {
    if (!sameCamera(clip, selectedCamera)) return false;
    if (!inDateRange(clip, from, to)) return false;
    if (variant && clipKind(clip) !== variant) return false;
    return true;
  }), [clips, selectedCamera, from, to, variant]);

  return (
    <MonitoringModulePage title="Grabaciones" subtitle="Clips manuales de prueba, crudos y con cajas de deteccion.">
      <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "100%" }}>
        <FilterBar>
          <TextField select label="Camara" value={selectedCameraId} onChange={(event) => setSelectedCameraId(event.target.value)} sx={{ gridColumn: { md: "span 3" } }}>
            <MenuItem value="">Todas</MenuItem>
            {cameras.map((camera) => <MenuItem key={camera.id || camera.name} value={camera.id}>{camera.display_name || camera.name}</MenuItem>)}
          </TextField>
          <TextField select label="Tipo" value={variant} onChange={(event) => setVariant(event.target.value)} sx={{ gridColumn: { md: "span 2" } }}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="raw">Crudo</MenuItem>
            <MenuItem value="boxes">Boxes</MenuItem>
          </TextField>
          <TextField label="Desde" type="date" value={from} onChange={(event) => setFrom(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ gridColumn: { md: "span 2" } }} />
          <TextField label="Hasta" type="date" value={to} onChange={(event) => setTo(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ gridColumn: { md: "span 2" } }} />
          <Box sx={{ gridColumn: { md: "span 3" }, display: "flex", justifyContent: { xs: "stretch", md: "flex-end" }, alignItems: "center" }}>
            <Button fullWidth={false} variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>
              Actualizar
            </Button>
          </Box>
        </FilterBar>

        <Grid container spacing={2}>
          {filtered.map((clip) => {
            const hasFile = Boolean(clip.id || directClipUrl(clip));
            const kind = clipKind(clip);
            const created = clip.started_at || clip.occurred_at || clip.created_at;
            return (
              <Grid item xs={12} md={6} lg={4} key={clip.id || `${clip.storage_key}-${kind}`}>
                <GlassCard hover={false} sx={{ p: 2.25, height: "100%" }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
                      <Typography variant="h5" noWrap>{clipCameraName(clip)}</Typography>
                      <Chip size="small" label={kind === "boxes" ? "Boxes" : kind === "raw" ? "Crudo" : kind} color={kind === "boxes" ? "primary" : "default"} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" noWrap>{clip.branch_name || "Sin sucursal"}</Typography>
                    <Typography variant="caption" color="text.secondary">{created ? new Date(created).toLocaleString("es-AR") : "Sin fecha"}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>{clip.storage_key || clip.public_url || "Sin archivo"}</Typography>
                    <Stack direction="row" gap={1}>
                      <Button size="small" variant="contained" startIcon={<PlayCircleOutlineIcon />} onClick={() => openClip(clip)} disabled={!hasFile}>Ver</Button>
                      <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={() => openClip(clip, { download: true })} disabled={!hasFile}>Descargar</Button>
                    </Stack>
                  </Stack>
                </GlassCard>
              </Grid>
            );
          })}
          {!filtered.length && (
            <Grid item xs={12}>
              <GlassCard hover={false}>
                <Typography color="text.secondary">{loading ? "Cargando grabaciones..." : "No hay grabaciones para los filtros seleccionados."}</Typography>
              </GlassCard>
            </Grid>
          )}
        </Grid>

        {selected && (
          <GlassCard hover={false} sx={{ mt: 2.5, p: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                <Typography variant="h5">{clipCameraName(selected)}</Typography>
                <Button onClick={() => setSelected(null)}>Cerrar</Button>
              </Stack>
              <Box sx={{ bgcolor: "#000", borderRadius: "8px", overflow: "hidden" }}>
                <video key={selected.playUrl} src={selected.playUrl} controls style={{ width: "100%", display: "block", maxHeight: 520 }} />
              </Box>
            </Stack>
          </GlassCard>
        )}

        <Snackbar open={snack.open} autoHideDuration={4200} onClose={() => setSnack({ ...snack, open: false })}>
          <Alert severity={snack.severity}>{snack.message}</Alert>
        </Snackbar>
      </Box>
    </MonitoringModulePage>
  );
};

export default RecordingsPage;
